import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, Pause, Play, Trash2 } from 'lucide-react';
import { IconClipboardList } from '@tabler/icons-react';
import { toast } from 'react-toastify';

import { useDeleteMicrostoreColumnMutation, useUpdateColumnInfoMutation } from 'data/__generated__';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type Props = {
    columnId: string;
    columnName: string;
    isPaused: boolean;
};

export const ColumnNodeActions = ({ columnId, columnName, isPaused }: Props) => {
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const [updateColumnInfo, { loading: updateLoading }] = useUpdateColumnInfoMutation();
    const [deleteColumn, { loading: deleteLoading }] = useDeleteMicrostoreColumnMutation();

    const handleTogglePause = async () => {
        const { data } = await updateColumnInfo({
            variables: {
                columnId,
                isPaused: !isPaused,
                removalDate: null,
                startingDate: null,
            },
        });
        const errors = data?.updateColumnInfoMutation?.errors;
        if (errors && errors.length > 0) {
            errors.forEach((error) => toast.error(`Erreur : ${error}`, { autoClose: false }));
            return;
        }
        toast.success(isPaused ? 'Colonne reprise' : 'Colonne mise en pause');
    };

    const handleDelete = async () => {
        const { data } = await deleteColumn({
            variables: { microstoreColumnId: columnId },
        });
        const errors = data?.deleteMicrostoreColumnMutation?.errors;
        if (errors && errors.length > 0) {
            errors.forEach((error) => toast.error(`Erreur : ${error}`, { autoClose: false }));
            return;
        }
        setConfirmDeleteOpen(false);
        toast.success('Colonne supprimée');
    };

    return (
        <>
            <Link to={`/microstore-column/${columnId}`} data-test="more-details">
                <IconClipboardList size={22} className="text-foreground" />
            </Link>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button size="icon-xs" variant="ghost" aria-label="Actions colonne">
                        <MoreVertical />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-44">
                    <DropdownMenuItem onSelect={handleTogglePause} disabled={updateLoading}>
                        {isPaused ? <Play /> : <Pause />}
                        <span>{isPaused ? 'Reprendre' : 'Mettre en pause'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        variant="destructive"
                        onSelect={(event) => {
                            event.preventDefault();
                            setConfirmDeleteOpen(true);
                        }}
                    >
                        <Trash2 />
                        <span>Supprimer</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Supprimer la colonne ?</DialogTitle>
                        <DialogDescription>
                            La colonne <strong>{columnName}</strong> sera définitivement supprimée. Cette action est
                            irréversible.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button">
                                Annuler
                            </Button>
                        </DialogClose>
                        <Button type="button" variant="destructive" onClick={handleDelete} disabled={deleteLoading}>
                            <Trash2 />
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
